import { Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router, ActivatedRouteSnapshot } from '@angular/router';
import { filter } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { environment } from '../../../environments/environment';
import { LOCALES, withLocalePrefix, stripLocalePrefix } from '../i18n/locales';
import { LocaleService } from './locale.service';
import { SeoApiService } from './seo-api.service';

export interface SeoInput {
  title: string;
  description: string;
  robots?: string;
  path?: string;
  image?: string | null;
  type?: 'website' | 'article';
  keywords?: string[];
  titleKey?: string;
  descriptionKey?: string;
  publishedAt?: string | null;
  modifiedAt?: string | null;
  section?: string | null;
  jsonLd?: unknown;
}

const INDEX = 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';
const LOGO = '/assets/img/car-finder/home/logo_velz_aumentada.png';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private started = false;

  constructor(
    private readonly router: Router,
    private readonly title: Title,
    private readonly meta: Meta,
    private readonly seoApi: SeoApiService,
    private readonly locale: LocaleService,
    private readonly transloco: TranslocoService,
  ) {}

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    document.documentElement.lang = this.locale.currentDef().htmlLang;
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.aplicarDaRota());

    this.transloco.events$
      .pipe(filter((event) => event.type === 'translationLoadSuccess'))
      .subscribe(() => {
        if (this.router.navigated) {
          this.aplicarDaRota();
        }
      });

    this.seoApi.publico().subscribe({
      next: () => {
        this.aplicarVerificacaoGoogle();
        if (this.router.navigated) {
          this.aplicarDaRota();
        }
      },
      error: () => {
        this.aplicarVerificacaoGoogle();
        if (this.router.navigated) {
          this.aplicarDaRota();
        }
      },
    });
  }

  apply(input: SeoInput): void {
    const path = input.path ?? this.caminhoAtual();
    const pathSemLocale = stripLocalePrefix(path);
    const def = this.locale.currentDef();
    document.documentElement.lang = def.htmlLang;
    const titulo = this.tituloCompleto(input.title);
    const canonical = this.absoluto(path);
    const imagem = this.absoluto(input.image || LOGO);
    const robots = input.robots ?? this.robotsDoCaminho(pathSemLocale);

    this.title.setTitle(titulo);
    this.meta.updateTag({ name: 'description', content: input.description });
    this.meta.updateTag({ name: 'robots', content: robots });
    this.meta.updateTag({ name: 'author', content: 'Velz' });
    this.definirOuRemover('keywords', input.keywords?.length ? input.keywords.join(', ') : null, 'name');

    this.meta.updateTag({ property: 'og:locale', content: def.ogLocale });
    this.meta.updateTag({ property: 'og:site_name', content: 'Velz' });
    this.meta.updateTag({ property: 'og:type', content: input.type ?? 'website' });
    this.meta.updateTag({ property: 'og:title', content: titulo });
    this.meta.updateTag({ property: 'og:description', content: input.description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({ property: 'og:image', content: imagem });

    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: titulo });
    this.meta.updateTag({ name: 'twitter:description', content: input.description });
    this.meta.updateTag({ name: 'twitter:image', content: imagem });

    this.definirOuRemover('article:published_time', input.publishedAt ?? null, 'property');
    this.definirOuRemover('article:modified_time', input.modifiedAt ?? null, 'property');
    this.definirOuRemover('article:section', input.section ?? null, 'property');
    this.substituirTags(input.type === 'article' ? input.keywords ?? [] : []);

    this.definirLink('canonical', canonical);
    this.limparAlternates();
    for (const loc of LOCALES) {
      const altPath = withLocalePrefix(pathSemLocale, loc.codigo);
      this.definirLink('alternate', this.absoluto(altPath), loc.hreflang);
      if (loc.codigo !== def.codigo) {
        this.meta.updateTag({ property: 'og:locale:alternate', content: loc.ogLocale });
      }
    }
    this.definirLink('alternate', this.absoluto(withLocalePrefix(pathSemLocale, 'pt-BR')), 'x-default');
    this.definirJsonLd(input.jsonLd ?? this.jsonLdPadrao(canonical, input.description));
  }

  absoluto(caminho: string): string {
    if (/^https?:\/\//i.test(caminho)) {
      return caminho;
    }
    const origem = window.location.origin;
    return origem + (caminho.startsWith('/') ? caminho : `/${caminho}`);
  }

  private aplicarDaRota(): void {
    const caminhoCompleto = this.caminhoAtual();
    const caminho = stripLocalePrefix(caminhoCompleto);
    if (caminho.startsWith('/auth')) {
      const tituloKey = caminho.includes('signup')
        ? 'seo.signup'
        : caminho.includes('forgot')
          ? 'seo.forgot'
          : caminho.includes('reset')
            ? 'seo.reset'
            : caminho.includes('verify')
              ? 'seo.verify'
              : 'common.entrar';
      this.apply({
        title: this.transloco.translate(tituloKey),
        description: this.transloco.translate('seo.acessoConta'),
        robots: 'noindex,nofollow',
        path: caminhoCompleto,
      });
      return;
    }

    if (caminho.startsWith('/account') || caminho.startsWith('/vendor')) {
      this.apply({
        title: this.transloco.translate('seo.conta'),
        description: this.transloco.translate('seo.contaDesc'),
        robots: 'noindex,nofollow',
        path: caminhoCompleto,
      });
      return;
    }

    if (caminho.startsWith('/catalog/single')) {
      this.apply({
        title: this.transloco.translate('seo.vehicle'),
        description: this.transloco.translate('seo.vehicleDesc'),
        robots: 'noindex,follow',
        path: caminhoCompleto,
      });
      return;
    }

    const seo = this.seoDaRota();
    if (seo) {
      this.apply({ ...seo, path: caminhoCompleto });
      return;
    }

    this.apply({
      title: this.transloco.translate('seo.notFoundTitle'),
      description: this.transloco.translate('seo.notFoundDesc'),
      robots: 'noindex,follow',
      path: caminhoCompleto,
    });
  }

  private seoDaRota(): SeoInput | null {
    let rota: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let seo: SeoInput | null = null;
    while (rota) {
      const data = rota.data?.['seo'] as SeoInput | undefined;
      if (data?.title && data.description) {
        seo = this.traduzirSeo(data);
      }
      rota = rota.firstChild;
    }
    return this.mesclarComApi(seo, this.caminhoAtual());
  }

  private traduzirSeo(data: SeoInput): SeoInput {
    return {
      ...data,
      title: data.titleKey ? this.transloco.translate(data.titleKey) : data.title,
      description: data.descriptionKey ? this.transloco.translate(data.descriptionKey) : data.description,
    };
  }

  private mesclarComApi(seo: SeoInput | null, caminho: string): SeoInput | null {
    const pathLookup = stripLocalePrefix(seo?.path ?? caminho);
    const api = this.seoApi.paginaPorLocale(pathLookup, this.locale.current());
    if (!api) {
      return seo;
    }
    const keywords = this.keywordsArray(api.keywords);
    if (!seo) {
      if (!api.title?.trim() || !api.description?.trim()) {
        return null;
      }
      return {
        title: api.title,
        description: api.description,
        keywords,
        path: api.path,
      };
    }
    return {
      ...seo,
      title: api.title?.trim() || seo.title,
      description: api.description?.trim() || seo.description,
      keywords: keywords ?? seo.keywords,
    };
  }

  private keywordsArray(keywords: string | null | undefined): string[] | undefined {
    if (!keywords?.trim()) {
      return undefined;
    }
    const lista = keywords
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
    return lista.length ? lista : undefined;
  }

  private aplicarVerificacaoGoogle(): void {
    const token =
      this.seoApi.atual()?.rastreamento.google_site_verification?.trim() ||
      environment.googleSiteVerification?.trim();
    if (!token) {
      return;
    }
    const existente = document.querySelector('meta[name="google-site-verification"]');
    if (existente) {
      existente.setAttribute('content', token);
      return;
    }
    this.meta.addTag({ name: 'google-site-verification', content: token });
  }

  private tituloCompleto(titulo: string): string {
    const limpo = titulo.trim();
    if (limpo === 'Velz' || /\|\s*Velz\b/.test(limpo)) {
      return limpo;
    }
    return `${limpo} | Velz`;
  }

  private robotsDoCaminho(caminho: string): string {
    if (this.eAreaFechada(caminho) || caminho.startsWith('/catalog/grid')) {
      return caminho.startsWith('/catalog/grid') ? 'noindex,follow' : 'noindex,nofollow';
    }
    return INDEX;
  }

  private eAreaFechada(caminho: string): boolean {
    return caminho.startsWith('/account') || caminho.startsWith('/auth') || caminho.startsWith('/vendor');
  }

  private caminhoAtual(): string {
    const url = this.router.url.split('?')[0].split('#')[0];
    if (url.length > 1 && url.endsWith('/')) {
      return url.slice(0, -1);
    }
    return url || '/';
  }

  private definirOuRemover(chave: string, valor: string | null, atributo: 'name' | 'property'): void {
    const seletor = `${atributo}="${chave}"`;
    if (!valor) {
      this.meta.removeTag(seletor);
      return;
    }
    this.meta.updateTag({ [atributo]: chave, content: valor });
  }

  private substituirTags(tags: string[]): void {
    while (this.meta.getTag('property="article:tag"')) {
      this.meta.removeTag('property="article:tag"');
    }
    for (const tag of tags) {
      this.meta.addTag({ property: 'article:tag', content: tag });
    }
  }

  private limparAlternates(): void {
    document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  }

  private definirLink(rel: string, href: string, hreflang?: string): void {
    const seletor = hreflang
      ? `link[rel="${rel}"][hreflang="${hreflang}"]`
      : `link[rel="${rel}"]:not([hreflang])`;
    let el = document.head.querySelector(seletor) as HTMLLinkElement | null;
    if (!el) {
      el = document.createElement('link');
      el.rel = rel;
      if (hreflang) {
        el.hreflang = hreflang;
      }
      document.head.appendChild(el);
    }
    el.href = href;
  }

  private definirJsonLd(dados: unknown): void {
    let el = document.getElementById('seo-jsonld') as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = 'seo-jsonld';
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(dados);
  }

  private jsonLdPadrao(canonical: string, description: string): unknown {
    const origem = window.location.origin;
    const caminho = this.caminhoAtual();
    const crumbs = caminho === '/'
      ? [{ '@type': 'ListItem', position: 1, name: 'Início', item: `${origem}/` }]
      : [
          { '@type': 'ListItem', position: 1, name: 'Início', item: `${origem}/` },
          { '@type': 'ListItem', position: 2, name: this.title.getTitle().replace(/\s*\|\s*Velz\s*$/, ''), item: canonical },
        ];
    return {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          name: 'Velz',
          url: origem,
          logo: this.absoluto(LOGO),
        },
        {
          '@type': 'WebSite',
          name: 'Velz',
          url: origem,
          inLanguage: 'pt-BR',
          description,
        },
        {
          '@type': 'WebPage',
          url: canonical,
          name: this.title.getTitle(),
          description,
          inLanguage: 'pt-BR',
          isPartOf: { '@type': 'WebSite', url: origem, name: 'Velz' },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: crumbs,
        },
      ],
    };
  }
}
